/**
 * Issue #766 — pure derivations for `components/GroundCard.jsx`, split out
 * for the same reason `lib/groundsNearYou.js` gives (its own header, and
 * `GroundsMap.test.js`'s): this repo mounts no components in unit tests, so
 * a rule written inside JSX is a rule nothing here can assert.
 *
 * Matched against `GroundCard.dc.html` (round 5, sha256 95c7daf0…) and
 * brief-766.md / D167 (`decisions-2026-09-24.md`, the orchestrator's
 * ruling on this slice — D167 wins over the issue text throughout).
 */
import { FACILITY_SURFACES, FACILITY_SURFACE_META } from "./facilities";
import { avgText, battedIn, battingAverage, notOutIn, numberToWords, toRateText } from "./recordFigures";
import { ballsToOvers, leftOutNote, oversToBalls, perOverRate, unreadableOversCount } from "./overs";
import { INELIGIBLE_COPY } from "./facilityRatings";
import { playedAtLabel } from "./logGroundStep";

/**
 * PR #921 review (F2c) — undrawn, invented copy. The artboard's own
 * default state is "a ground played three times" and draws no zero-match
 * line at all; `lib/logGroundStep.js`'s `playedAtLabel(0)` ("Saved · not
 * played yet") is the wrong borrow for this card — that sentence belongs
 * to the log flow's ground PICKER, talking about a bookmark, and reusing
 * it here states a fact ("Saved") that may not be true of a ground opened
 * from "Nearby" that the player has never saved either. This is its own
 * sentence, honest about the one thing the card actually knows.
 */
export const NOT_PLAYED_HERE_LABEL = "Not played here yet.";

const ISO_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Issue #768 — this card's own copy of the API's comparison rule
 * (`latestPlayedDate`, `api/src/db/repos/grounds.ts`): a `match_date`
 * counts only if, after trimming, it is exactly `YYYY-MM-DD`; the latest is
 * the greatest such string; a set with no valid date resolves to `null`. A
 * separate small export, unit-tested with the same cases, rather than
 * importing server code across the api/frontend boundary — the card
 * derives its own record client-side (issue #766 ruling 2) and must apply
 * the identical rule to agree with the log flow's ground step, which gets
 * its date from the API field instead.
 */
export function latestPlayedDate(rawDates) {
  let latest = null;
  for (const raw of rawDates || []) {
    if (raw == null) continue;
    const trimmed = String(raw).trim();
    if (!ISO_DATE_SHAPE.test(trimmed)) continue;
    if (latest === null || trimmed > latest) latest = trimmed;
  }
  return latest;
}

/**
 * PR #921 review (F2a) — the exact words for a facilities or matches read
 * that failed, never folded into an empty/zero answer. Held here so the
 * card's own sections cannot drift into describing one failure two ways.
 */
export const FACTS_LOAD_FAILED = "Couldn't load the facts for this ground.";
export const RECORD_LOAD_FAILED = "Couldn't load your record here.";

/**
 * PR #921 review (F2c) — the "Played here" line's text, pulled out as its
 * own pure function so the zero-match branch is unit-testable without
 * mounting `GroundCard.jsx`: `matchCount > 0` reuses the shipped
 * `playedAtLabel` (`lib/logGroundStep.js`) verbatim; `matchCount === 0`
 * is `NOT_PLAYED_HERE_LABEL`, never that function's OWN zero case
 * ("Saved · not played yet"), which states a fact — a bookmark — this
 * card's own ground may not have.
 *
 * Issue #768 (D167 G5) — `lastPlayed` passes straight through to
 * `playedAtLabel`, which decides whether it actually shows.
 */
export function playedHereLabel(matchCount, lastPlayed) {
  return matchCount > 0 ? playedAtLabel(matchCount, lastPlayed) : NOT_PLAYED_HERE_LABEL;
}

/**
 * PR #921 review (F2d) — "Rate the surface"'s own helper line, as a pure
 * function of the SAME eligibility the rating control itself reads
 * (`can_rate`, `useGroundRatings`/`api/src/lib/facilityRatings.ts`), never
 * the drawing's "You've played here, so you can" unconditionally.
 *
 * `canRate` is `null`/`undefined` while the ratings read is still in
 * flight (or failed) — the drawing states nothing until it knows, rather
 * than guessing eligibility from silence.
 */
export function rateSurfaceHelperText(canRate) {
  if (canRate == null) return "";
  return canRate ? "You've played here, so you can" : INELIGIBLE_COPY;
}

/**
 * D167 G9 — the four fact tiles (`Fields`, `Nets`, `Indoor`, `Floodlights`),
 * derived the same way the played list's own `groundFactsLine`
 * (`lib/groundsNearYou.js`) derives them: a count for the first two, and
 * "Yes" the instant ANY one facility carries the flag for the other two —
 * never a per-facility breakdown, and never defaulted when the ground
 * genuinely has no facilities recorded (both counts are then honestly `0`,
 * which `FacilityList`'s own empty-state copy is what names the absence,
 * not this function inventing a dash).
 */
export function groundFactTiles(facilities) {
  const rows = (facilities || []).filter(Boolean);
  const fields = rows.filter((f) => f.kind === "field").length;
  const nets = rows.filter((f) => f.kind === "net").length;
  const indoor = rows.some((f) => f.is_indoor === true);
  const floodlights = rows.some((f) => f.has_lighting === true);
  return { fields, nets, indoor, floodlights };
}

/**
 * D167 G9 — the ONE Surface value the summary tile shows: the distinct
 * surfaces recorded across every facility at this ground, in
 * `FACILITY_SURFACES` vocabulary order (the same order
 * `groundFactsLine`/`nearbyFactChips` already list them), title-cased —
 * this card's tile draws "Turf", not `groundFactsLine`'s own lower-cased
 * sentence fragment ("turf"), a different screen's own convention.
 *
 * `null` when nothing is recorded at all — a ground with no facilities has
 * no surface to report, and `FacilityList`'s own empty-state copy is what
 * names that, not a dash invented here.
 */
export function groundSurfaceLabel(facilities) {
  const rows = (facilities || []).filter(Boolean);
  const present = FACILITY_SURFACES.filter((s) => rows.some((f) => f.surface === s));
  if (present.length === 0) return null;
  return present.map((s) => FACILITY_SURFACE_META[s].label).join(" · ");
}

/**
 * D167 G4/G5/G6 — the player's own record at ONE ground, from a set of
 * `GET /matches` rows already known to belong to the caller (issue #766
 * ruling 2: derived client-side, filtered to `groundId`; no API field,
 * and this function never fetches — the caller fetches once per open and
 * hands the array in).
 *
 * PR #921 review (F2b) — `matchCount` is every match at this ground,
 * batting or not, the SAME count `GET /venues/played` states for the
 * played list this card was opened from (`matches` on that row): a match
 * where the player only bowled or fielded is still a match played here,
 * and "Played here N times" must never disagree with the list behind it.
 * `innings` is the narrower, batting-only count `battingAverage`'s own
 * `innings` input expects (`recordFigures.js`) — it can be smaller than
 * `matchCount`, and the two answer different questions on purpose.
 *
 * D184 (issue #963, redrawing the "next round" this comment used to
 * promise): the average prints to ONE decimal (`avgText`), matching the
 * drawing's own "31.7" exactly — G4's finding (two decimals, "31.67") is
 * what this issue closes.
 *
 * G6: `isNotOutEveryTime` is true only for the shape where there IS a real
 * BATTING record here (at least one innings) and every one of them was
 * not out — never for a ground never batted at all, whether or not the
 * player has played (fielded/bowled) there. "Nothing to divide by because
 * you were never out" and "nothing to divide by because you have never
 * batted here" are different claims, and only the first is this card's
 * drawn not-out sentence.
 */
export function groundBattingRecord(matches, groundId) {
  const atGround = (matches || []).filter((m) => m && m.ground_id === groundId);
  const matchCount = atGround.length;
  // Issue #768 (D167 G5) — the latest valid match_date among every match at
  // this ground, batting or not (the same set matchCount counts).
  const lastPlayed = latestPlayedDate(atGround.map((m) => m.match_date));
  // #945 — was `m.batting && m.batting.runs != null`, which counted a
  // pre-#715 (runs: 0, balls_faced: 0, "Not Out") row as an innings: a
  // blank form's zero-coerced counters, indistinguishable from a real 0
  // not out, on a match the player may never have batted in at all.
  // `battedIn`/`notOutIn` (recordFigures.js, mirroring statsCompute.ts's
  // #873 rule) read it the same way the API's own fold does.
  const battingRows = atGround.filter(battedIn);
  const innings = battingRows.length;
  const totalRuns = battingRows.reduce((sum, m) => sum + (Number(m.batting.runs) || 0), 0);
  const notOuts = battingRows.filter(notOutIn).length;
  const avg = battingAverage({ total_runs: totalRuns, innings, not_outs: notOuts });
  return {
    matchCount,
    totalRuns,
    innings,
    notOuts,
    dismissals: avg.dismissals,
    averageValue: avg.value,
    averageText: avgText(avg.value),
    lastPlayed,
    isNotOutEveryTime: innings > 0 && avg.value == null && avg.dismissals === 0,
    inningsWords: numberToWords(innings),
  };
}

/**
 * #1291 (D214 L1, round-12 GroundCardFigures) — the four figures under
 * "Your record here": Matches, Runs, Wickets, Economy, from the same
 * `GET /matches` rows the card already holds. Runs and wickets are `null`
 * (a dash, never 0) when there is nothing to add up. Economy rests only on
 * spells whose overs read as over.ball notation (D190); `restsOn` says how
 * many overs that is, and names any spell left out (D260).
 */
export function groundFigures(matches, groundId) {
  const atGround = (matches || []).filter((m) => m && m.ground_id === groundId);
  const batted = atGround.filter(battedIn);
  const runs = batted.length > 0 ? batted.reduce((sum, m) => sum + (Number(m.batting.runs) || 0), 0) : null;
  // A spell is a row that bowled (overs > 0), as the API's per-ground fold
  // reads it. Wickets sum over every spell; only the overs-based rate and the
  // overs line rest on the readable ones (D260).
  const bowled = atGround.filter((m) => Number(m.bowling?.overs) > 0);
  const spells = bowled.filter((m) => oversToBalls(m.bowling.overs) !== null);
  const balls = spells.reduce((sum, m) => sum + oversToBalls(m.bowling.overs), 0);
  const wickets = bowled.length > 0 ? bowled.reduce((sum, m) => sum + (Number(m.bowling.wickets) || 0), 0) : null;
  const conceded = spells.reduce((sum, m) => sum + (Number(m.bowling.runs_conceded) || 0), 0);
  const economy = perOverRate(conceded, balls);
  const oversText = balls > 0 ? ballsToOvers(balls) : null;
  const leftOut = unreadableOversCount(atGround);
  let restsOn = balls > 0 ? `Rests on ${oversText} over${oversText === 1 ? "" : "s"}` : null;
  if (restsOn && leftOut > 0) restsOn += ` (${leftOutNote(leftOut)})`;
  else if (!restsOn && leftOut > 0) restsOn = `(${leftOutNote(leftOut)})`;
  return { matchCount: atGround.length, runs, wickets, economy, economyText: toRateText(economy), oversText, restsOn, leftOut };
}
